import Joi from '@hapi/joi';
import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { SurveyStates } from '../../../../common/src/enums/SurveyStates';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import {
    Content,
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    Panel,
    PanelBody,
    PanelButton,
    PanelTopHeading,
    TopbarCrumbActive,
    TopbarLeftOnly,
    EditPanelFilterSelect,
    Tray,
} from '../elements';
import { IElement } from '../interfaces/IElement';
import {
    ValidatingEditPanelSaveButton,
    ValidatingFeedback,
    ValidatingFilterDropdownWithUrl,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormDependentSection,
    ValidatingFormInputText,
    ValidatingFormTextArea,
} from '../validatingElements';
import { ElementSelection } from './ElementSelection';
import { hasStringValue, hasValue } from './existanceCheck';

export const projectSchema = Joi.object().keys({
    name: Joi.string().required(),
    description: Joi.string().allow(null, ''),
    objectId: Joi.string().required(),
    surveyId: Joi.string().required(),
    elementId: Joi.string().required(),
    unitId: Joi.string().allow(null, ''),
    manifestationId: Joi.string().allow(null, ''),
});

interface IProject {
    id: string;
    clientName: string;
    clientCompanyId: string;
}

function elementFilter(element: IElement) {
    return element.isUtResultPlaceholder;
}

export function ImportProject(props: { params: { id?: string } }) {
    const translate = useTranslate();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { formatDate } = useIntl();
    const navigate = useNavigate();

    const [ownerCompanyId, setOwnerCompanyId] = React.useState<string | null>(null);
    const [ownerCompanies, setOwnerCompanies] = React.useState<{ id: string; name: string }[]>([]);

    React.useEffect(() => {
        const fetchData = async () => {
            const projectResponse = await fetchGet(`/api/utprojects/${props.params.id}`);
            const project = projectResponse.json as IProject;

            const companiesResponse = await fetchGet('/api/companyList?type=owner');

            const company = companiesResponse.json.items.find((i: { name: string }) => i.name.toLowerCase() === project.clientName.toLowerCase());
            if (company) {
                setOwnerCompanyId(company.id);
            }
            setOwnerCompanies(companiesResponse.json.items);
        };

        void fetchData();
    }, [props.params.id]);

    async function handleOnFormDataChanged(
        key: string,
        value: string,
        _item: Record<string, unknown>,
        onChange: (key: string, value: any) => Promise<void>,
    ) {
        if (key === 'objectId') {
            if (!hasValue(value)) {
                await onChange('surveyId', null);
            }
        }
    }

    function handleSavedOrCancelled() {
        navigate('/projects');
    }

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title="UT Projects" icon="fa-bar-chart-o" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    <Panel topBorder>
                        <PanelTopHeading>{translate('project.import')}</PanelTopHeading>
                        <PanelBody noPadding>
                            <ValidatingForm
                                schema={projectSchema}
                                itemUrl={`/api/utprojects/${props.params.id}`}
                                id={props.params.id}
                                onSubmitted={handleSavedOrCancelled}
                                defaultItem={{ ownerCompanyId: null, objectId: null, surveyId: null }}
                                onChange={handleOnFormDataChanged}
                            >
                                <EditPanel>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>{translate('common.name')}</EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <ValidatingFormInputText name="name" placeholder={translate('common.name')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>{translate('common.description')}</EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <ValidatingFormTextArea name="description" placeholder={translate('common.description')} />
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>{translate('object.owner')}</EditPanelCell>
                                        <EditPanelCell rowCount={8}>
                                            <EditPanelFilterSelect
                                                name="ownerCompanyId"
                                                value={ownerCompanyId}
                                                handleChange={(value) => setOwnerCompanyId(value)}
                                                placeholder={translate('common.selectItem', translate('common.company'))}
                                                options={ownerCompanies.map((item) => ({ value: item.id, text: item.name }))}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <ValidatingFormDependentSection showIf={() => hasStringValue(ownerCompanyId)}>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={4}>{translate('common.object')}</EditPanelCell>
                                            <EditPanelCell rowCount={8}>
                                                <ValidatingFilterDropdownWithUrl
                                                    url={`/api/objects?companyId=${ownerCompanyId}`}
                                                    name="objectId"
                                                    dataTextField="name"
                                                    dataValueField="id"
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    </ValidatingFormDependentSection>
                                    <ValidatingFormDependentSection showIf={(item) => hasValue(item.objectId)}>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={4}>{translate('common.survey')}</EditPanelCell>
                                            <ValidatingFormContext.Consumer>
                                                {(state) => (
                                                    <EditPanelCell rowCount={8}>
                                                        <ValidatingFilterDropdownWithUrl
                                                            url={`/api/objects/${state.item.objectId}/surveys?states=${SurveyStates.Open},${SurveyStates.Preparation},${SurveyStates.Conducting},${SurveyStates.Reporting}`}
                                                            getOptionLabel={(o) =>
                                                                `${formatDate(new Date(o.surveryedOn))} ${o.description.substring(0, 250)}`
                                                            }
                                                            name="surveyId"
                                                            dataTextField="name"
                                                            dataValueField="id"
                                                        />
                                                    </EditPanelCell>
                                                )}
                                            </ValidatingFormContext.Consumer>
                                        </EditPanelRow>
                                    </ValidatingFormDependentSection>
                                    <ValidatingFormDependentSection showIf={(item) => hasValue(item.surveyId)}>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={4}>{translate('common.element')}:</EditPanelCell>
                                            <EditPanelCell rowCount={8}>
                                                <ValidatingFormContext.Consumer>
                                                    {(state) => (
                                                        <>
                                                            <label className="field">
                                                                <ElementSelection
                                                                    elementFilter={elementFilter}
                                                                    surveyId={state.item.surveyId}
                                                                    elementId={state.item.elementId}
                                                                    unitId={state.item.unitId}
                                                                    ignoreManifestations={true}
                                                                    onChange={async (values) => {
                                                                        await state.onChange('elementId', values.elementId);
                                                                        await state.onChange('unitId', values.unitId);
                                                                    }}
                                                                />
                                                            </label>
                                                            <ValidatingFeedback error={state.errors.get('elementId')} />
                                                        </>
                                                    )}
                                                </ValidatingFormContext.Consumer>
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    </ValidatingFormDependentSection>
                                </EditPanel>
                                <EditPanelButtons footer>
                                    <PanelButton text={translate('common.cancel')} onClick={handleSavedOrCancelled} />
                                    <ValidatingEditPanelSaveButton />
                                </EditPanelButtons>
                            </ValidatingForm>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
